import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, Calendar, Eye, Sparkles, Loader2 } from 'lucide-react';
import api, { getCategories, likePost, unlikePost, trackVisit } from '../utils/api';
import { t } from '../utils/translations';
import LazyImage from '../components/LazyImage';
import TerminalWindow from '../components/TerminalWindow';
import ThemeChangeModal from '../components/ThemeChangeModal';
import { useTheme } from '../contexts/ThemeContext';

const TYPING_SPEED = 60; // ms per character

function getUTMSource() {
  const params = new URLSearchParams(window.location.search);
  return params.get('utm_source');
}

const Home = () => {
  const [posts, setPosts] = useState([]);
  const [idMap, setIdMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [command, setCommand] = useState('');
  const [output, setOutput] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showThemeModal, setShowThemeModal] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { setTheme, themes, currentTheme, isInitialized } = useTheme();
  
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/posts/?page=${currentPage}`);
        let postsData = [];
        let count = 0;
        if (Array.isArray(response.data)) {
          postsData = response.data;
        } else if (response.data && Array.isArray(response.data.results)) {
          postsData = response.data.results;
          count = response.data.count || 0;
        } else if (response.data && typeof response.data === 'object') {
          postsData = [response.data];
        }
        setPosts(postsData);
        // Build id map
        const map = {};
        postsData.forEach((post, idx) => {
          map[(idx + 1).toString()] = post;
        });
        setIdMap(map);
        // Calculate total pages
        if (count && response.data.results) {
          setTotalPages(Math.ceil(count / response.data.results.length));
        } else {
          setTotalPages(1);
        }
      } catch (err) {
        setError('Failed to fetch posts');
        setPosts([]);
        setIdMap({});
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, [currentPage]);

  useEffect(() => {
    inputRef.current && inputRef.current.focus();
  }, []);

  const stats = {
    posts: posts.length,
    views: posts.reduce((sum, p) => sum + (p.views || 0), 0),
    comments: posts.reduce((sum, p) => sum + (p.comments_count || 0), 0),
  };

  const handleCommand = (cmd) => {
    const trimmed = cmd.trim();
    console.log('Debug - Command received:', `"${trimmed}"`);
    console.log('Debug - useTheme available:', { currentTheme, hasSetTheme: !!setTheme, hasThemes: !!themes });
    
    if (trimmed === 'help') {
      setOutput(
        <div className="mt-4 text-[#cdd6f4]">
          <div>Available commands:</div>
          <ul className="list-disc pl-6">
            <li><span className="text-[#a6e3a1]">help</span>: Show this help message</li>
            <li><span className="text-[#a6e3a1]">ls</span>: List all post IDs</li>
            <li><span className="text-[#a6e3a1]">stat &lt;id&gt;</span>: Show summary for a post by ID</li>
            <li><span className="text-[#a6e3a1]">vim &lt;id&gt;</span>: Read a post by ID</li>
            <li><span className="text-[#a6e3a1]">hist</span>: Show history/help for exiting vim</li>
            <li><span className="text-[#a6e3a1]">theme &lt;name&gt;</span>: Change terminal theme (lolo, default)</li>
            <li><span className="text-[#a6e3a1]">reset</span>: Reset to default theme</li>
            <li><span className="text-[#a6e3a1]">clear</span>: Clear the output</li>
          </ul>
        </div>
      );
    } else if (trimmed === 'reset') {
      console.log('Debug - Reset command detected!');
      if (!isInitialized || !setTheme) {
        setOutput(<div className="mt-4 text-[#f38ba8]">Theme system not ready, please try again in a moment</div>);
        return;
      }
      setTheme('default');
      setOutput(
        <div className="mt-4">
          <div className="text-[#a6e3a1]">✨ Theme reset to default</div>
          <div className="text-[#cdd6f4] text-sm mt-1">Back to the classic green terminal theme</div>
        </div>
      );
    } else if (trimmed.startsWith('theme ')) {
      console.log('Debug - Theme command detected!');
      
      // Check if theme context is ready
      if (!isInitialized || !setTheme || !themes) {
        console.log('Debug - Theme context not ready, isInitialized:', isInitialized);
        setOutput(<div className="mt-4 text-[#f38ba8]">Theme system not ready, please try again in a moment</div>);
        return;
      }
      
      const themeName = trimmed.replace('theme ', '').trim();
      console.log('Debug - Theme name extracted:', `"${themeName}"`);
      
      if (themeName === '') {
        setOutput(
          <div className="mt-4">
            <div className="text-[#cdd6f4] mb-2">Current theme: <span className="text-[#a6e3a1]">{currentTheme}</span></div>
            <div className="text-[#cdd6f4]">Available themes:</div>
            <ul className="list-disc pl-6 text-[#cdd6f4]">
              {Object.entries(themes).map(([key, theme]) => (
                <li key={key}>
                  <span className="text-[#a6e3a1]">{key}</span>: {theme.description}
                </li>
              ))}
            </ul>
            <div className="text-xs text-[#39c5bb] mt-2">Usage: <span className="bg-[#23272e] px-1 py-0.5 rounded text-[#e5e5e5]">theme &lt;name&gt;</span></div>
          </div>
        );
      } else if (themes[themeName]) {
        console.log('Debug - Setting theme to:', themeName);
        
        if (themeName === 'lolo') {
          // Show both the theme change message AND the modal for lolo theme
          setTheme(themeName);
          setOutput(
            <div className="mt-4">
              <div className="text-[#a6e3a1]">✨ Theme changed to: {themes[themeName].name}</div>
              <div className="text-[#cdd6f4] text-sm mt-1">{themes[themeName].description}</div>
            </div>
          );
          // Show modal after a brief delay so user sees the theme change message first
          setTimeout(() => {
            setShowThemeModal(true);
          }, 1500);
        } else {
          // Regular theme change for other themes
          setTheme(themeName);
          setOutput(
            <div className="mt-4">
              <div className="text-[#a6e3a1]">✨ Theme changed to: {themes[themeName].name}</div>
              <div className="text-[#cdd6f4] text-sm mt-1">{themes[themeName].description}</div>
            </div>
          );
        }
      } else {
        console.log('Debug - Theme not found:', themeName, 'Available:', Object.keys(themes));
        setOutput(
          <div className="mt-4">
            <div className="text-[#f38ba8]">Theme '{themeName}' not found</div>
            <div className="text-[#cdd6f4] mt-1">Available themes: {Object.keys(themes).join(', ')}</div>
          </div>
        );
      }
    } else if (trimmed === 'ls') {
      setOutput(
        <div className="mt-4 text-[#cdd6f4]">
          {posts.map((post, idx) => (
            <div key={post.slug}><span className="text-[#a6e3a1]">[{idx + 1}]</span></div>
          ))}
        </div>
      );
    } else if (trimmed.startsWith('stat ')) {
      const id = trimmed.replace('stat ', '').trim();
      const post = idMap[id];
      if (post) {
        setOutput(
          <div className="mt-4">
            <div className="flex flex-col gap-2 mb-2">
              <span className="text-lg font-bold text-[#e5e5e5]">{post.title}</span>
              <span className="text-xs text-[#39c5bb]">{post.published_at || post.date}</span>
            </div>
            <div className="mb-2 text-[#e5e5e5]">{post.excerpt}</div>
            <div className="text-xs text-[#39c5bb] mt-2">Hint: use <span className="bg-[#23272e] px-1 py-0.5 rounded text-[#e5e5e5]">vim {id}</span> to read this post.</div>
          </div>
        );
      } else {
        setOutput(<div className="mt-4 text-[#f38ba8]">No such post with ID: {id}</div>);
      }
    } else if (trimmed.startsWith('vim')) {
      const parts = trimmed.split(/\s+/);
      if (parts.length !== 2 || !/^[0-9]+$/.test(parts[1])) {
        setOutput(<div className="mt-4 text-[#f38ba8]">usage: vim &lt;id&gt;</div>);
        return;
      }
      const id = parts[1];
      const post = idMap[id];
      if (post) {
        navigate(`/posts/${post.slug}`);
      } else {
        setOutput(<div className="mt-4 text-[#f38ba8]">No such post with ID: {id}</div>);
      }
    } else if (trimmed === 'hist') {
      setOutput(
        <div className="mt-4 text-[#cdd6f4]">
          <div>To exit vim, type <span className="bg-[#23272e] px-1 py-0.5 rounded text-[#e5e5e5]">:q</span></div>
        </div>
      );
    } else if (trimmed === 'clear') {
      setOutput(null);
    } else {
      setOutput(<div className="mt-4 text-[#f38ba8]">command not found: {trimmed}</div>);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleCommand(command);
    setCommand('');
  };

  // Terminal-style pagination controls
  const Pagination = () => {
    if (totalPages <= 1) return null;
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(i);
    }
    return (
      <div className="flex items-center justify-center gap-2 mt-8 font-mono">
        <button
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className={`px-3 py-1 rounded bg-transparent border border-[#181825] text-[#89b4fa] hover:bg-[#181825] hover:text-[#a6e3a1] transition-all duration-150 ${currentPage === 1 ? 'opacity-40 cursor-not-allowed' : ''}`}
        >
          prev
        </button>
        {pageNumbers.map((num) => (
          <button
            key={num}
            onClick={() => setCurrentPage(num)}
            className={`px-3 py-1 rounded border border-[#181825] mx-1 font-bold ${currentPage === num ? 'bg-[#181825] text-[#a6e3a1] border-[#a6e3a1]' : 'bg-transparent text-[#89b4fa] hover:bg-[#181825] hover:text-[#a6e3a1] hover:border-[#a6e3a1]'} transition-all duration-150`}
          >
            {num}
          </button>
        ))}
        <button
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
          className={`px-3 py-1 rounded bg-transparent border border-[#181825] text-[#89b4fa] hover:bg-[#181825] hover:text-[#a6e3a1] transition-all duration-150 ${currentPage === totalPages ? 'opacity-40 cursor-not-allowed' : ''}`}
        >
          next
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#181a1b] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 font-mono">
          <div className="flex items-center gap-2">
            <svg className="animate-spin h-6 w-6 text-[#00ff66]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" strokeOpacity="0.2" /><path d="M12 2a10 10 0 0 1 10 10" /></svg>
            <span className="text-[#00ff66] text-lg">abdelhadi@blog:~$</span>
            <span className="text-[#e5e5e5] ml-2">Loading posts...</span>
          </div>
          <div className="text-[#39c5bb] text-sm mt-2">Please wait while we fetch the latest stories.</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#181a1b] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 font-mono">
          <div className="flex items-center gap-2">
            <span className="text-[#ff5555] text-lg">abdelhadi@blog:~$</span>
            <span className="text-[#e5e5e5] ml-2">Error loading posts.</span>
          </div>
          <div className="text-[#ff5555] text-sm mt-2">{error}</div>
          <button 
            onClick={() => window.location.reload()}
            className="mt-4 px-6 py-2 bg-[#ff5555] text-[#181a1b] rounded-xl font-semibold hover:bg-[#ff8888] transition-all duration-200"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-start py-4 sm:py-12 px-1 sm:px-4 md:px-8 bg-[#181a1b]" dir="ltr">
      <TerminalWindow 
        prompt="abdelhadi@blog:~$" 
        className="w-full max-w-4xl"
        isInteractive={true}
        command={command}
        onCommandChange={e => setCommand(e.target.value)}
        onCommandSubmit={handleSubmit}
        commandInputRef={inputRef}
        placeholder="Type vim <id> to read a post"
      >
        {/* Terminal content */}
        <div className="flex flex-col gap-6 font-mono animate-fadein text-left overflow-x-auto">
          {/* Welcome message */}
          <div className="text-[#00ff66] text-base mb-4">Type <span className="bg-[#23272e] px-2 py-1 rounded text-[#e5e5e5]">vim &lt;id&gt;</span> to read a post.</div>
          {/* Blog statistics */}
          <div className="text-xs sm:text-sm text-[#39c5bb] mb-2">
            # Blog Statistics: <span className="text-[#00ff66]">Posts: {stats.posts}</span> | <span className="text-[#00ff66]">Views: {stats.views}</span> | <span className="text-[#00ff66]">Comments: {stats.comments}</span>
          </div>
          {/* Output from prompt */}
          {output}
          {/* Posts */}
          {posts.map((post, idx) => (
            <div
              key={post.slug}
              className="terminal-output flex flex-col gap-1 p-2 sm:p-4 rounded-lg transition-all duration-300 hover:bg-[#181a1b] hover:shadow-lg hover:shadow-[#00ff66]/20 group cursor-pointer animate-fadein border border-transparent hover:border-[#39c5bb]/30"
              style={{ animationDelay: `${0.1 * idx}s` }}
            >
              <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 sm:gap-x-2 flex-nowrap overflow-x-auto text-xs sm:text-base">
                <span className="terminal-prompt text-[#39c5bb] font-bold mr-1 sm:mr-2">abdelhadi@blog:~$</span>
                <span className="text-[#00ff66]">[{idx + 1}] stat {idx + 1}</span>
              </div>
              <Link to={`/posts/${post.slug}`} className="text-base sm:text-lg font-bold text-[#e5e5e5] group-hover:text-[#00ff66] transition-colors duration-200 underline underline-offset-2 hover:no-underline break-words sm:truncate">
                {post.title}
              </Link>
              <span className="text-xs text-[#39c5bb]">{post.published_at || post.date}</span>
              <span className="text-xs sm:text-base text-[#e5e5e5] group-hover:text-[#bdbdbd] transition-colors duration-200 break-words line-clamp-4 sm:line-clamp-none">{post.excerpt}</span>
            </div>
          ))}
          {/* Pagination */}
          <Pagination />
        </div>
      </TerminalWindow>
      {/* Subtle background pattern */}
      <div 
        className="pointer-events-none fixed inset-0 z-0 opacity-10" 
        style={{
          background: currentTheme === 'lolo' 
            ? 'radial-gradient(circle at 60% 40%, #c084fc 0%, transparent 60%)' 
            : 'radial-gradient(circle at 60% 40%, #00ff66 0%, transparent 60%)'
        }} 
      />
      <style>{`
        @keyframes fadein { from { opacity: 0; transform: translateY(16px);} to { opacity: 1; transform: none; } }
        .animate-fadein { animation: fadein 0.7s cubic-bezier(.4,1.7,.6,.97) both; }
        .terminal-cursor { display: inline-block; width: 1ch; animation: blink 1s steps(1) infinite; }
        @keyframes blink { 0%, 50% { opacity: 1; } 51%, 100% { opacity: 0; } }
        /* Responsive tweaks for blog post cards */
        @media (max-width: 640px) {
          .terminal-output { padding: 0.5rem !important; font-size: 0.95rem !important; }
          .terminal-output .text-base, .terminal-output .sm\\:text-lg { font-size: 1rem !important; }
          .terminal-output .text-xs, .terminal-output .sm\\:text-base { font-size: 0.85rem !important; }
        }
      `}</style>
      <ThemeChangeModal 
        isOpen={showThemeModal}
        onClose={() => setShowThemeModal(false)}
        themeName="lolo"
        message="You are my everything, you are my first and last love. I didn't think I'll ever be obsessed like this, that I will do anything to make you happy. A queen like you deserves more than this, but as the nerdy me, this is what I know, but I'll always try to be the man that you want and make you happy. You are my life, you have no idea how obsessed I am with you. I can't wait for the day for you to be my wife and I'll work without stop to achieve that. 💜"
      />
    </div>
  );
};

export default Home; 